import React, {
	ChangeEvent,
	ReactElement,
	RefObject,
	useEffect,
	useMemo,
	useRef,
	useState
} from 'react';
import { InputBase, Paper } from '@material-ui/core';

import { SearchSuggestions } from '../../../redux';
import { getSearchHistory, isValidSearchValue } from '../../../services';
import { SearchFieldDropdown } from '../../organisms';
import { useSearch } from '../../../hooks/useSearch';
import { useTranslation } from 'react-i18next';

import './search-field.styles.scss';

export interface ISearchFieldProps {
	setFocus: (focus: boolean) => void;
}

export function SearchField(props: ISearchFieldProps): ReactElement {
	const { setFocus } = props;
	const {
		searchValue,
		setSearchValue,
		suggestions,
		disableDropdown,
		placeholder,
		autoFocus
	} = useSearch();
	const { t } = useTranslation('search');

	const [searchFieldFocus, setSearchFieldFocus] = useState<boolean>(false);
	const searchFieldRef: RefObject<HTMLDivElement> =
		useRef<HTMLDivElement>(null);
	const inputRef: RefObject<HTMLInputElement> =
		useRef<HTMLInputElement>(null);

	const displaySuggestions: SearchSuggestions = useMemo(() => {
		if (searchValue.length > 0) return suggestions;
		return { resultPreviews: [], suggestions: [] };
	}, [searchValue, suggestions]);

	useEffect(() => {
		document.addEventListener('focusin', handleFocusInEvent);
		document.addEventListener('focusout', handleFocusOutEvent);

		return () => {
			document.removeEventListener('focusin', handleFocusInEvent);
			document.removeEventListener('focusout', handleFocusOutEvent);
		};
	}, []);

	useEffect(() => {
		if (!inputRef.current || !autoFocus) return;
		inputRef.current.focus();
	}, [searchValue]);

	function handleFocusInEvent(event: FocusEvent): void {
		const isSearchFocussed: boolean = isElementInOrEqualToSearchField(
			event.target as HTMLElement
		);
		setSearchFieldFocus(isSearchFocussed);
		setFocus(isSearchFocussed);
	}

	function handleFocusOutEvent(event: FocusEvent): void {
		const searchLostFocus: boolean = isElementInOrEqualToSearchField(
			event.target as HTMLElement
		);
		const newFocusInSearch: boolean = isElementInOrEqualToSearchField(
			event.relatedTarget as HTMLElement
		);

		if (searchLostFocus && !newFocusInSearch) {
			setSearchFieldFocus(false);
			setFocus(false);
		}
	}

	function isElementInOrEqualToSearchField(element: HTMLElement): boolean {
		if (!searchFieldRef.current) return false;

		return (
			element === searchFieldRef.current ||
			searchFieldRef.current.contains(element)
		);
	}

	function onSearchChange(event: ChangeEvent<HTMLInputElement>): void {
		const value: string = event.target.value;
		setSearchValue(value);
	}

	function hasSearchHistory(): boolean {
		return getSearchHistory().length > 0;
	}

	function hasSuggestions(): boolean {
		return (
			displaySuggestions.resultPreviews.length > 0 ||
			displaySuggestions.suggestions.length > 0
		);
	}

	function hasSufficientSearchValue(): boolean {
		return searchValue.length < 1 || isValidSearchValue(searchValue);
	}

	function dropdownIsOpen(): boolean {
		return (
			searchFieldFocus &&
			!disableDropdown &&
			hasSufficientSearchValue() &&
			(hasSearchHistory() || hasSuggestions())
		);
	}

	return (
		<div
			ref={searchFieldRef}
			className="search-field"
			data-mdat-module="search-field"
		>
			<InputBase
				inputRef={inputRef}
				className="search-field-input"
				value={searchValue}
				onChange={onSearchChange}
				placeholder={
					placeholder ? placeholder : t('searchFieldPlaceholder')
				}
				data-mdat-item-id="search-input"
			/>
			{dropdownIsOpen() && (
				<Paper
					className="search-field-dropdown-container"
					data-mdat-module="search-suggestions-dropdown"
				>
					<SearchFieldDropdown
						onClick={() => {
							setSearchFieldFocus(false);
						}}
					/>
				</Paper>
			)}
		</div>
	);
}
